import Image from 'next/image'
import { Intercom } from 'react-live-chat-loader'
import { Header } from '../components/marketing/Header'
import Meta from '../components/Meta'
import { Marketing, SubHeadline } from '../designsystem/Text'

export default function About() {
  return (
    <div className='bg-gray-50'>
      <Intercom color='#608BD8' />
      <Header />
      <Meta
        title='About Us – GraphCDN'
        description='Founded by Tim Suchanek and Max Stoiber, GraphCDN is backed by a set of world-class operators and investors.'
        imageSrc='https://graphcdn.io/tim-and-max.jpg'
        imageHeight={2785}
        imageWidth={5570}
      />
      <div className='max-w-4xl px-6 pt-12 pb-32 mx-auto space-y-8 text-center sm:px-6 sm:pt-24'>
        <Marketing.H1>
          Building a great product shouldn’t be stressful
        </Marketing.H1>

        <Marketing.SubHeadline className='max-w-4xl'>
          We’ve spent many sleepless nights worrying about scaling, uptime,
          security and performance in our career. Now, we want to take care of
          that for you, so you can focus on building a great product instead.
        </Marketing.SubHeadline>
      </div>
      <main className='max-w-6xl px-6 mx-auto space-y-32 text-center sm:px-6'>
        <div>
          <div className='overflow-hidden shadow-big rounded-2xl'>
            <Image
              src='/tim-and-max.jpg'
              alt=''
              layout='responsive'
              width={1392}
              height={696}
              priority
            />
          </div>
          <div className='text-center'>
            <p className='mx-auto mt-3 text-base font-medium sm:text-lg text-gray gotham opacity-60 md:text-xl md:mt-5'>
              <a
                href='https://twitter.com/TimSuchanek'
                className='underline'
                target='_blank'
                rel='noopener noreferrer'
              >
                Tim Suchanek
              </a>{' '}
              and{' '}
              <a
                href='https://twitter.com/mxstbr'
                className='underline'
                target='_blank'
                rel='noopener noreferrer'
              >
                Max Stoiber
              </a>
              , the co-founders of GraphCDN
            </p>
          </div>
        </div>
        <Investors />
        <Careers />
      </main>
    </div>
  )
}

function Careers() {
  return (
    <div>
      <SubHeadline>Careers</SubHeadline>
      <Marketing.H2>Work at GraphCDN</Marketing.H2>
      <div className='max-w-xl mx-auto mt-4 space-y-4 text-lg text-left text-gray opacity-80 gotham'>
        <p>
          You love GraphQL and want to work at a fully remote startup? Come join
          us at GraphCDN! We are hiring across engineering, infrastructure,
          design, sales, marketing, customer success and beyond.
        </p>
        <p>
          Official job postings coming soon. Until then,{' '}
          <a
            href='mailto:founders@graphcdn.io?subject=I want to work at GraphCDN'
            className='underline transition-opacity hover:opacity-80'
          >
            write Tim &amp; Max an email
          </a>{' '}
          and tell us how you think you could fit in!
        </p>
        <p>
          <strong>Future founder promise:</strong> If you want to found your own
          startup eventually, but aren't quite ready yet, come work at GraphCDN
          for a bit. We'll teach you everything we know about starting something
          new. Then, once you are ready to found your own thing, we'll be your
          first angels and introduce you to all our investors!
        </p>
      </div>
    </div>
  )
}

function Investors() {
  return (
    <div className='flex flex-col space-y-16 sm:items-center sm:space-y-20'>
      <div>
        <SubHeadline>Our investors</SubHeadline>
        <Marketing.H2>Backed by industry leaders</Marketing.H2>
      </div>
      <ul className='grid grid-cols-1 gap-x-16 gap-y-8 sm:gap-y-16 md:grid-cols-2 lg:grid-cols-3'>
        {investors.map((person) => (
          <li key={person.name}>
            <a href={getLink(person)} className='flex items-center'>
              <Image
                className='flex-shrink-0 rounded-full'
                src={getAvatar(person)}
                alt=''
                layout='intrinsic'
                height={80}
                width={80}
              />
              <div className='flex-1 ml-4 space-y-2 text-left'>
                <div className='text-base font-medium gotham text-gray'>
                  <h3 className='text-xl font-medium leading-tight text-gray gotham hover:underline'>
                    {person.name}
                  </h3>
                  <p className='opacity-60'>{person.role}</p>
                </div>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

const investors = [
  {
    name: 'Max Claussen',
    role: 'Investor, System.One',
    twitter: 'MaxClaussen',
    link: 'http://www.systemone.vc',
  },
  {
    name: 'Guillermo Rauch',
    role: 'CEO, Vercel',
    twitter: 'rauchg',
  },
  {
    name: 'Tom Preston-Werner',
    role: 'Creator, RedwoodJS',
    twitter: 'mojombo',
  },
  {
    name: 'Andreas Klinger',
    role: 'CTO, On Deck',
    twitter: 'andreasklinger',
  },
  {
    name: 'Matt Biilmann',
    role: 'Co-CEO, Netlify',
    twitter: 'biilmann',
  },
  {
    name: 'Christian Bach',
    role: 'Co-CEO, Netlify',
    twitter: 'Chr_Bach',
  },
  {
    name: 'Jason Warner',
    role: 'CTO, GitHub',
    twitter: 'jasoncwarner',
  },
  {
    name: 'Nicolas Dessaigne',
    role: 'Co-founder, Algolia',
    twitter: 'dessaigne',
  },
  {
    name: 'Jed Watson',
    role: 'Creator, KeystoneJS',
    twitter: 'jedwatson',
  },
  { name: 'Boris Bozic', role: 'Co-CEO, Thinkmill', twitter: 'borisbozic' },
  {
    name: 'Moritz Plassnig',
    role: 'CGO, Immuta',
    twitter: 'moritzplassnig',
  },
  {
    name: 'Manuel Weiss',
    role: 'Co-founder, Codeship',
    twitter: 'manualwise',
  },
  {
    name: 'Shinji Kim',
    role: 'Founder, Select Star',
    twitter: 'shinjikim',
  },
  {
    name: 'Johannes Schickling',
    role: 'Co-founder, Prisma',
    twitter: 'schickling',
  },
  {
    name: 'Søren Bramer Schmidt',
    role: 'Co-founder, Prisma',
    twitter: 'sorenbs',
  },
  { name: 'Ellen Chisa', role: 'Co-founder, Darklang', twitter: 'ellenchisa' },
  {
    name: 'Marc Mengler',
    role: 'CEO, Understand.ai',
    image: '/marcmengler.jpeg',
    link: 'https://www.linkedin.com/in/marcmengler',
  },
  {
    name: 'Maykel Loomans',
    role: 'Principal Designer, Brex',
    twitter: 'miekd',
  },
  {
    name: 'Brian Lovin',
    role: 'Staff Designer, GitHub',
    twitter: 'brian_lovin',
  },
  {
    name: 'Iheanyi Ekechukwu',
    role: 'Senior Eng, Planetscale',
    twitter: 'kwuchu',
  },
  {
    name: 'Bryn Jackson',
    role: 'Founder, Along Video',
    twitter: 'superbryntendo',
  },
  { name: 'Ha Duong', role: 'Angel investor', twitter: 'haduong_' },
  {
    name: 'Phil Möhring',
    role: 'Investor, TinyVC',
    twitter: 'pmoe',
    link: 'https://www.tiny.vc',
  },
  {
    name: 'Andy Chung',
    role: 'Investor, TinyVC',
    image: '/andychung.jpeg',
    link: 'https://www.tiny.vc',
  },
  {
    name: 'Liu Jiang',
    role: 'Angel investor',
    image: '/liujiang.jpeg',
    link: 'https://www.linkedin.com/in/liujiang1',
  },
  { name: 'Jeremy Yap', role: 'Angel investor', twitter: 'jtotheyap' },
]

const getAvatar = (person) => {
  if (person.twitter)
    return `https://res.cloudinary.com/graphcdn/image/twitter_name/${person.twitter}.jpg`

  return person.image || ''
}

const getLink = (person) => {
  if (person.link) return person.link
  if (person.twitter) return `https://twitter.com/${person.twitter}`

  return ''
}
